import React from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Toaster } from '@theonefamily/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
const HubBar = React.lazy(() => import('hubBar/HubBar').then(m => ({ default: m.HubBar })));
import { Shield, Globe, Bot, Cloud, Mail, AlertTriangle, Fish, Settings, CloudCog, ShieldAlert, Users, Fingerprint, MapPin, ClipboardList, Bell, Brain, BarChart2, UserX, Navigation, BookOpen, Phone, Newspaper, Search, Briefcase, Award, UserCheck, Link, Key, FileText, LayoutDashboard, ShieldCheck } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/dark-web', label: 'Dark Web', icon: Globe },
  { to: '/shadow-ai', label: 'Shadow AI', icon: Bot },
  { to: '/saas-posture', label: 'SaaS Posture', icon: Cloud },
  { to: '/dmarc', label: 'DMARC', icon: Mail },
  { to: '/email-domains', label: 'Email Domains', icon: Shield },
  { to: '/email-alerts', label: 'Email Alerts', icon: AlertTriangle },
  { to: '/phishing', label: 'Phishing', icon: Fish },
  { to: '/breach-response', label: 'Breach Response', icon: Bell },
  { to: '/adaptive-learning', label: 'Adaptive Learning', icon: Brain },
  { to: '/msp-risk', label: 'MSP Risk', icon: BarChart2 },
  { to: '/insider-threat', label: 'Insider Threat', icon: UserX },
  { to: '/casb', label: 'CASB Dashboard', icon: CloudCog },
  // P1
  { to: '/dmarc-journey', label: 'DMARC Journey', icon: Navigation },
  { to: '/compliance-training', label: 'Compliance Training', icon: BookOpen },
  { to: '/vishing', label: 'Vishing Simulation', icon: Phone },
  { to: '/security-briefing', label: 'Security Briefing', icon: Newspaper },
  // P2
  { to: '/lookalike-domains', label: 'Lookalike Domains', icon: Search },
  { to: '/bec-simulation', label: 'BEC Simulation', icon: Briefcase },
  { to: '/security-culture', label: 'Security Culture', icon: Award },
  { to: '/user-onboarding', label: 'User Onboarding', icon: UserCheck },
  // P3
  { to: '/supply-chain-risk', label: 'Supply Chain Risk', icon: Link },
  { to: '/credential-exposure', label: 'Credential Exposure', icon: Key },
  { to: '/threat-narrative', label: 'Threat Narrative', icon: FileText },
  { to: '/casb/events', label: 'CASB Events', icon: ShieldAlert },
  { to: '/casb/user-risk', label: 'CASB User Risk', icon: Users },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const ZERO_TRUST_NAV = [
  { to: '/zero-trust', label: 'Conditional Access', icon: Fingerprint },
  { to: '/zero-trust/locations', label: 'Known Locations', icon: MapPin },
  { to: '/zero-trust/audit', label: 'Access Audit', icon: ClipboardList },
];

const CYPHER_NAV = [
  { to: '/cypher', label: 'Cypher Training', icon: ShieldCheck },
];

export default function App() {
  return (
    <ErrorBoundary>
    <div className="min-h-screen bg-gray-50">
      <Toaster />
      <React.Suspense fallback={<div style={{ height: 44 }} />}>
        <HubBar
          currentProduct="protect"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonestack.com'}
          hubUrl={import.meta.env.VITE_HUB_URL}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'protect',
            platformName: 'The One Protect',
          }}
        />
      </React.Suspense>
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-emerald-600" />
          <h1 className="text-xl font-bold text-gray-900">The One Protect</h1>
        </div>
      </header>
      <div className="flex">
        <nav className="w-56 bg-white border-r border-gray-200 min-h-[calc(100vh-73px)] p-4">
          <ul className="space-y-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="px-3 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Training</p>
            <ul className="space-y-1">
              {CYPHER_NAV.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="px-3 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Zero Trust</p>
            <ul className="space-y-1">
              {ZERO_TRUST_NAV.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === '/zero-trust'}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
    </ErrorBoundary>
  );
}
